import React, { useState } from 'react';
import { Box, Button, Grid, Typography, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PageWrapper from 'components/Common/PageWraper';
import PageHeader from 'components/Header/Page';
import SearchableDropdown, {
  type SearchableDropdownOption,
} from 'components/Dropdown/SearchableDropdown';
import PepwiseTextInput from 'components/Common/PepwiseTextInput/PepwiseTextInput';
import DynamicLabelValueFields, {
  type DynamicFieldItem,
} from 'components/Inputs/DynamicLabelValueFields';
import GlobalSearchContainer from 'components/GlobalSearch/GlobalSearchContainer';
import { Dayjs } from 'dayjs';

const ownerOptions: SearchableDropdownOption[] = [
  {
    label: 'Sunil Sharma',
    subLabel: 'sunil.sharma1@pepsico.com',
    value: 'sunil-sharma',
  },
  {
    label: 'Amit Verma',
    subLabel: 'amit.verma@pepsico.com',
    value: 'amit-verma',
  },
  {
    label: 'Riya Menon',
    subLabel: 'riya.menon@pepsico.com',
    value: 'riya-menon',
  },
  {
    label: 'Arjun Nair',
    subLabel: 'arjun.nair@pepsico.com',
    value: 'arjun-nair',
  },
];

export default function FormInputsPage() {
  const navigate = useNavigate();
  const [selectedOption, setSelectedOption] = useState<SearchableDropdownOption | null>(
    ownerOptions[0],
  );
  const [textValue, setTextValue] = useState('Sample Text');
  const [readOnlyTextValue, setReadOnlyTextValue] = useState('Read-only Text');

  const [fields, setFields] = React.useState<DynamicFieldItem[]>([
    { id: '1', label: '', value: '' },
  ]);

  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);

  const handleSave = (updatedFields: DynamicFieldItem[]) => {
    console.log('Saved fields:', updatedFields);
  };

  const titleSx = {
    fontFamily: 'Montserrat, sans-serif',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '25px',
    color: '#000000',
  };

  return (
    <PageWrapper>
      <PageHeader title="Form Input Fields Demo">
        <Button variant="pepwiseSecondary" onClick={() => navigate(-1)}>
          ← Back
        </Button>
      </PageHeader>

      <Grid container spacing={2} mt={2} px={2}>
        {/* Original Dropdown Demo */}
        <Grid item xs={12} md={4}>
          <SearchableDropdown
            label="Owner"
            value={selectedOption}
            setValue={setSelectedOption}
            options={ownerOptions}
            placeholder="Owner"
            handleChange={(selected) => {
              console.log('Selected option:', selected);
            }}
          />
        </Grid>

        <Grid
          item
          xs={12}
          sm={4}
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <GlobalSearchContainer />
        </Grid>

        {/* Separator */}
        <Grid item xs={12}>
          <Box sx={{ borderTop: '1px solid #E3E8EF', my: 2 }} />
        </Grid>

        {/* New Text Input States Demo */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#001E3E' }}>
            Text Input States (PepWise 2.0)
          </Typography>

          <Grid container spacing={4}>
            {/* ROW 1 */}
            <Grid item xs={12} md={3}>
              <Stack spacing={1}>
                <Typography sx={titleSx}>Idle</Typography>
                <PepwiseTextInput
                  label="Text"
                  type="text"
                  value={textValue}
                  onChange={(e) => setTextValue(e.target.value)}
                  placeholder="Enter text"
                />
              </Stack>
            </Grid>

            <Grid item xs={12} md={3}>
              <Stack spacing={1}>
                <Typography sx={titleSx}>Read-Only</Typography>
                <PepwiseTextInput
                  label="Text"
                  type="text"
                  value={readOnlyTextValue}
                  readOnly
                  placeholder="Enter text"
                />
              </Stack>
            </Grid>

            <Grid item xs={12} md={3}>
              <Stack spacing={1}>
                <Typography sx={titleSx}>Disabled</Typography>
                <PepwiseTextInput
                  label="Text"
                  type="text"
                  value={textValue}
                  disabled
                  placeholder="Enter text"
                />
              </Stack>
            </Grid>

            <Grid item xs={12} md={3}>
              <Stack spacing={1}>
                <Typography sx={titleSx}>Error</Typography>
                <PepwiseTextInput
                  label="Error"
                  type="text"
                  value={textValue}
                  onChange={(e) => setTextValue(e.target.value)}
                  error={textValue.trim() === ''}
                  helperText={textValue.trim() === '' ? 'This field is required' : ''}
                  placeholder="Enter text"
                />
              </Stack>
            </Grid>

            {/* DATE INPUTS */}
            <Grid item xs={12}>
              <Typography sx={{ ...titleSx, fontWeight: 700, mt: 2 }}>Date</Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <Stack spacing={1}>
                <Typography sx={titleSx}>Idle</Typography>
                <PepwiseTextInput
                  label="Date"
                  type="date"
                  value={selectedDate ? selectedDate.format('DD MMM, YYYY') : ''}
                  placeholder="Select date"
                  selectedDate={selectedDate}
                  onDateChange={setSelectedDate}
                />
              </Stack>
            </Grid>

            <Grid item xs={12} md={3}>
              <Stack spacing={1}>
                <Typography sx={titleSx}>Read-Only</Typography>
                <PepwiseTextInput
                  label="Date"
                  type="date"
                  value={selectedDate ? selectedDate.format('DD MMM, YYYY') : ''}
                  placeholder="Select date"
                  selectedDate={selectedDate}
                  readOnly
                />
              </Stack>
            </Grid>

            <Grid item xs={12} md={3}>
              <Stack spacing={1}>
                <Typography sx={titleSx}>Disabled</Typography>
                <PepwiseTextInput
                  label="Date"
                  type="date"
                  value={selectedDate ? selectedDate.format('DD MMM, YYYY') : ''}
                  placeholder="Select date"
                  selectedDate={selectedDate}
                  disabled
                />
              </Stack>
            </Grid>

            <Grid item xs={12} md={3}>
              <Stack spacing={1}>
                <Typography sx={titleSx}>Error</Typography>
                <PepwiseTextInput
                  label="Error"
                  type="date"
                  value={selectedDate ? selectedDate.format('DD MMM, YYYY') : ''}
                  placeholder="Select date"
                  selectedDate={selectedDate}
                  onDateChange={setSelectedDate}
                  error={!selectedDate}
                  helperText={!selectedDate ? 'This field is required' : ''}
                />
              </Stack>
            </Grid>

            <Grid item xs={12} md={12}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#000000' }}>
                Dynamic Label value fields
              </Typography>
              <DynamicLabelValueFields fields={fields} setFields={setFields} onSave={handleSave} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </PageWrapper>
  );
}
